import { useIsMdOrGreater } from '@dreamstack/feature-components/src/hooks/useWindowSize'
import { SimpleMuiButton } from '@dreamstack/feature-components/src/layout/SimpleMuiButton'
import { HeadingH3 } from '@dreamstack/feature-components/src/layout/Texts'
import { useFormatToCurrencyCutoff, useTranslation } from '@dreamstack/i18n'
import { ChevronDown } from '@dreamstack/icons/src/icon-components/ChevronDown'
import { ChevronUp } from '@dreamstack/icons/src/icon-components/ChevronUp'
import { cn } from '@dreamstack/util/src/cn'
import filter from 'lodash/filter'
import forEach from 'lodash/forEach'
import map from 'lodash/map'
import round from 'lodash/round'
import dynamic from 'next/dynamic'
import React, { type FunctionComponent } from 'react'
import { calculateRelativeChange } from '../RegionGraphs/libs/helperFunctions'
import { getGraphLegendsColorId } from '../WohnKostenReport/libs/getGraphLegendsColorId'
import { getSelectedidentifiers } from '../WohnKostenReport/libs/getSelectedIdentifiers'
import { GraphControls } from './GraphControls'
import { GraphLegends } from './GraphLegends'
import { useControlsForBarGraphData } from './useControlsForBarGraphData'
import { useGraphFontsizeCb } from './useGraphFontsize'

const ResponsiveBar = dynamic(
  () => import('@nivo/bar').then((mod) => mod.ResponsiveBar),
  {
    ssr: false,
  }
)

export const MobileControls: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  ...rest
}) => <div className={cn('md:hidden', className)} {...rest} />
export const DesktopControls: React.FC<
  React.HTMLAttributes<HTMLDivElement>
> = ({ className, ...rest }) => (
  <div className={cn('hidden md:block', className)} {...rest} />
)

export const BarGraph: FunctionComponent<
  React.PropsWithChildren<{
    data: any[] | undefined
    title: string
    graphControlXLabel?: string
    graphControlYLabel?: string
    hideXControls?: boolean
    hideYControls?: boolean
    controledSelectedIdentifiers?: string[]
    controledSelectedXValues?: string[]
    colors: string[]
    marginAxisLeft?: number
  }>
> = ({
  data,
  title,
  graphControlXLabel,
  graphControlYLabel,
  hideXControls,
  hideYControls,
  colors,
  controledSelectedIdentifiers,
  controledSelectedXValues,
  marginAxisLeft = 70, // default value for axis left margin, 70 is min marging so that a 6 digit number fits in the axis can be displayed on mobile,
}) => {
  const formatToDecimal = useFormatToCurrencyCutoff()

  const {
    setShowControls,
    setSelectedXValues,
    setSelectedIdentifiers,
    showControls,
    possibleX,
    possibleIdentifiers,
    selectedXValues,
    selectedIdentifiers,
  } = useControlsForBarGraphData(
    data,
    controledSelectedIdentifiers,
    controledSelectedXValues
  )

  const graphLegend = getGraphLegendsColorId(possibleIdentifiers, colors)

  const filteredByPossibleX = filter(data, (d) =>
    selectedXValues.includes(d.id)
  )
  const finalData = map(filteredByPossibleX, (d) => {
    const result: { [key: string]: string } = { id: d.id }
    forEach(Object.keys(d), (key) => {
      if (selectedIdentifiers.includes(key)) {
        result[key] = d[key]
      }
    })
    return result
  })

  const graphFontSize = useGraphFontsizeCb()
  const md = useIsMdOrGreater()
  let marginForGraph = md
    ? { top: 50, right: 120, bottom: 64, left: marginAxisLeft + 20 }
    : { top: 50, right: 40, bottom: 90, left: marginAxisLeft }
  const t = useTranslation()

  const GraphControlsForX = (
    <GraphControls
      graphControlAllLabel={graphControlXLabel}
      possibleValues={possibleX}
      selectedValues={selectedXValues}
      setArray={setSelectedXValues}
    />
  )

  const GraphControlsForY = (
    <GraphControls
      graphControlAllLabel={graphControlYLabel}
      possibleValues={possibleIdentifiers}
      selectedValues={selectedIdentifiers}
      setArray={setSelectedIdentifiers}
    />
  )
  const valuesToShow = ['Berlin', 'Frankfurt am Main', 'Köln', 'München']
  return (
    <div className="col-span-full lg:col-start-3 lg:col-span-8">
      <HeadingH3 className="mb-px-48">{title}</HeadingH3>
      <div className="space-y-px-12">
        {/* TODO: Better solution for controls on mobile? */}
        {!(hideXControls && hideYControls) && (
          <MobileControls>
            <div className="space-y-px-16">
              <SimpleMuiButton
                onClick={() => {
                  setShowControls(!showControls)
                }}
                color={'primary'}
                variant={'text'}
                endIcon={showControls ? <ChevronUp /> : <ChevronDown />}
              >
                {t('accentro:graphControls.configureControls')}
              </SimpleMuiButton>
              {showControls && (
                <>
                  {!hideXControls && GraphControlsForX}
                  {!hideYControls && GraphControlsForY}
                </>
              )}
            </div>
          </MobileControls>
        )}
        <DesktopControls>
          {!hideXControls && GraphControlsForX}
          {!hideYControls && GraphControlsForY}
        </DesktopControls>
      </div>
      <div className="h-px-576 overflow-hidden">
        <ResponsiveBar
          enableLabel={false}
          theme={{
            text: {
              fontSize: graphFontSize(),
              fontFamily: 'Montserrat',
            },
          }}
          colors={colors}
          keys={possibleIdentifiers}
          indexBy={'id'}
          data={finalData || []}
          padding={0.5}
          valueScale={{ type: 'linear' }}
          indexScale={{ type: 'band', round: true }}
          margin={marginForGraph}
          enableGridX={false}
          valueFormat={formatToDecimal}
          axisTop={null}
          groupMode="grouped"
          innerPadding={5}
          axisRight={null}
          axisBottom={{
            tickSize: 8,
            tickPadding: 12,
            tickRotation: md ? 0 : 30,
            legendOffset: 36,
            legendPosition: 'middle',
            format: (v) => (valuesToShow.find((vts) => vts === v) ? v : ''),
          }}
          axisLeft={{
            format: (value) => formatToDecimal(value),
            tickSize: 8,
            tickPadding: 12,
            tickRotation: 0,
            legendOffset: -40,
            legendPosition: 'middle',
          }}
          layers={[
            'grid',
            'axes',
            'bars',
            'markers',
            'legends',
            'annotations',
            ({ bars }) => {
              if (!md) return null

              return (
                <g>
                  {bars.map(({ width, height, y, x, data }, index) => {
                    const relativeChange = calculateRelativeChange(
                      Number(data.data['2011']),
                      Number(data.data['2021'])
                    )
                    // only show this custom outer label on bars that are too small
                    return data.id === '2021' ? (
                      <text
                        key={index}
                        transform={`translate(${x}, ${y - 10})`}
                        // textAnchor="right"
                        // dominantBaseline="central"
                      >
                        {(relativeChange > 0 ? '+' : '') +
                          t('accentro:graphControls.percentage', {
                            percentage: round(relativeChange),
                          })}
                      </text>
                    ) : null
                  })}
                </g>
              )
            },
          ]}
        />
      </div>
      <div className="flex justify-center">
        <GraphLegends
          identifiers={getSelectedidentifiers(graphLegend, selectedIdentifiers)}
        />
      </div>
    </div>
  )
}
